import styled from 'styled-components'
import { stoColor, stoMedia, stoRadius, stoShadow, stoSpace, stoType } from 'constants/stoTheme'

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${stoSpace[4]};

  > * {
    align-self: stretch;
  }
`

/** Figma BMN2: what the chart shows, above the card. */
export const Heading = styled.div`
  display: flex;
  flex-direction: column;
`

export const HeadingTitle = styled.p`
  ${stoType.bodyLg};
  margin: 0;
  font-weight: 600;
  color: ${stoColor.textPrimary};
`

/** The tab card's own frame below tablet: one border, the chart and stats edge to edge in it. */
export const flatInTabCard = `
  ${stoMedia.belowTablet} {
    margin: -${stoSpace[4]};
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
`

/** A size container: the fund stats under the chart lay out by the card's own width. */
export const ChartCard = styled.div`
  container-type: inline-size;
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  box-shadow: ${stoShadow.xs};
  ${flatInTabCard}
`

export const ChartArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[2]};
  padding: ${stoSpace[6]};

  ${stoMedia.belowDesktop} {
    padding: ${stoSpace[4]};
  }
`

export const ChartHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${stoSpace[4]};
`

/** The view switch and the currency switch, side by side at the start of the header row. */
export const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${stoSpace[2]};
`

/** One legend entry per line; two when a chart compares (BMN1 vs buy and hold). */
export const Legends = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${stoSpace[4]};
`

export const Legend = styled.p`
  display: flex;
  align-items: center;
  gap: ${stoSpace[2]};
  margin: 0;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0.4px;
  color: ${stoColor.textPrimary};

  ${stoMedia.belowDesktop} {
    font-weight: 400;
  }
`

/** Figma fund card: the legend sits 24px under the view switch (the area's own gap is 8px). */
export const FundLegend = styled(Legend)`
  margin-top: ${stoSpace[4]};
`

export const LegendDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: ${stoRadius.full};
  background: ${({ $color }) => $color};
`

/**
 * Figma: three stats under a rule, separated by 1px lines. Three in a row while each gets
 * ~210px (label + info icon), else one per row (never 2 + 1). The 1px gap over a border-coloured
 * background draws every divider, as in MetricGrid; the bottom corners follow the card's radius.
 */
export const Stats = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  border-top: 1px solid ${stoColor.borderSubtle};
  border-radius: 0 0 calc(${stoRadius.lg} - 1px) calc(${stoRadius.lg} - 1px);
  background: ${stoColor.borderSubtle};

  ${stoMedia.belowTablet} {
    border-radius: 0;
  }

  @container (max-width: 629px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

export const Stat = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${stoSpace[4]};
  background: ${stoColor.surface};
`

export const StatLabel = styled.p`
  ${stoType.statLabel};
  display: flex;
  align-items: center;
  gap: ${stoSpace[2]};
  margin: 0;
  color: ${stoColor.textSecondary};
`

export const StatValue = styled.p`
  ${stoType.metricValue};
  margin: 0;
  color: ${stoColor.textPrimary};
  font-variant-numeric: tabular-nums;
`

/** "Download CSV" inside the footnote: reads as an underlined word, acts as a button. */
export const InlineAction = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 600;
  letter-spacing: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`
